import { AppError } from '@/domain/errors/app-error'
import { i18nTranslationHelper } from '@/i18n'
import errorMonitor from '@/infra/tracking/error-monitor'
import theme from '@/presentation/assets/styles/theme'
import { ValidAccountState } from '@/presentation/contexts/auth'
import { reloadPage, WINDOW_REL, WINDOW_TARGET } from '@/presentation/helpers'
import useAccount from '@/presentation/hooks/auth/use-account'
import errorState from '@images/error-state.svg'
import { Button, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Link, useAsyncError, useRouteError } from 'react-router-dom'

export interface GenericErrorProps {
  fullScreen?: boolean
  title?: string
  message?: string
  image?: string | undefined
  reloadButtonLabel?: string
  onReloadClick?: () => void
}

const GenericError = ({
  fullScreen,
  title,
  message,
  image = errorState,
  reloadButtonLabel,
  onReloadClick
}: GenericErrorProps) => {
  const [account] = useAccount() as ValidAccountState
  const t = i18nTranslationHelper.getTComponents
  title = title ?? t('genericError.title')
  message = message ?? t('genericError.message')

  const classStyle = `w-full flex flex-col justify-center items-center ${
    fullScreen ? 'h-[100vh]' : ''
  }`

  const errorAsync = useAsyncError() as AppError
  const errorRoute = useRouteError() as AppError
  const error = errorAsync || errorRoute

  const handleReload = () => {
    if (onReloadClick) {
      onReloadClick()
    } else {
      reloadPage()
    }
  }

  useEffect(() => {
    if (!error) return
    errorMonitor.captureException(error)
  }, [error])

  return (
    <div className={classStyle}>
      <div className="text-center max-w-lg px-5">
        <img src={image} />
        <Typography variant="h6" className="pt-4">
          {error?.customError && error.title ? error.title : title}
        </Typography>
        <Typography variant="subtitle1" className="py-4" sx={{ whiteSpace: 'pre-wrap' }}>
          {error?.customError && error.message ? error.message : message}
        </Typography>
        <div className="flex flex-col gap-3 items-center">
          <Button variant="outlined" color="primary" className="w-fit" onClick={handleReload}>
            {reloadButtonLabel ?? t('genericError.reloadButton')}
          </Button>
          <Button
            component={Link}
            to={account?.helpCenterConfig?.redirectTo}
            target={WINDOW_TARGET.BLANK}
            rel={WINDOW_REL.NO}
            variant="text"
            className="w-fit"
            sx={{ color: theme.palette.common.black }}
          >
            {t('genericError.cta')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default GenericError
